import { Fragment, useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Modal, ModalHeader, ModalBody, Container } from "reactstrap";
import Breadcrumb from "../common/breadcrumb";
import { fetchBranches } from "../../utils/apiCalls";
import { getAnprEvents, getDriveThruMeta, fetchAnprSnapshotObjectUrl } from "../../utils/driveThruApi";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import { RCard, Badge, Loader as RLoader } from "../reporting/ReportingShared";

const STATUS_CHIP = {
  matched: { color: C.green, bg: C.greenLight },
  no_match: { color: C.textMed, bg: C.bg },
  low_confidence: { color: C.orange, bg: C.orangeLight },
  no_plate: { color: C.textMed, bg: C.bg },
  camera_error: { color: C.coral, bg: C.coralLight },
  provider_error: { color: C.coral, bg: C.coralLight },
};

const PAGE_SIZE = 20;

const filterInputStyle = {
  padding: "7px 12px", fontSize: 12, borderRadius: 8, border: `1.5px solid ${C.border}`,
  fontFamily: "inherit", outline: "none", color: C.text, background: C.white,
};
const coralBtn = {
  background: C.coral, border: "none", borderRadius: 10, fontWeight: 600, fontSize: 13,
  color: C.white, padding: "8px 18px", cursor: "pointer",
};
const pageBtn = (disabled) => ({
  background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, fontWeight: 600, fontSize: 12,
  color: disabled ? C.textLight : C.textMed, padding: "6px 14px", cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.6 : 1,
});

const StatusChip = ({ status }) => {
  const c = STATUS_CHIP[status] || STATUS_CHIP.no_match;
  return <Badge color={c.color} bg={c.bg}>{status}</Badge>;
};

const AnprEvents = () => {
  const userType = localStorage.getItem("userType");
  const merchantId = localStorage.getItem("MerchantId");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
  const selectedMerchant = userType === "merchant" ? merchantId : reduxSelectedMerchant;

  const [branches, setBranches] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [filters, setFilters] = useState({ branchId: "", plate: "", status: "", from: "", to: "" });
  const [events, setEvents] = useState([]);
  const [pagination, setPagination] = useState({ pageNo: 1, totalPages: 0, totalRecords: 0 });
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState({ open: false, event: null, snapshotUrl: null, loadingImg: false });

  useEffect(() => {
    (async () => {
      const res = await fetchBranches({}, false);
      let list = res?.data || [];
      if (selectedMerchant && selectedMerchant !== "all") {
        list = list.filter((b) => String(b.MerchantId) === String(selectedMerchant));
      }
      setBranches(list);
      const meta = await getDriveThruMeta();
      setStatuses(meta.eventStatuses || []);
    })();
  }, [selectedMerchant]);

  const loadEvents = useCallback(
    async (page = 1) => {
      setLoading(true);
      const params = { page, pageSize: PAGE_SIZE };
      if (filters.branchId) params.branchId = filters.branchId;
      if (filters.plate) params.plate = filters.plate;
      if (filters.status) params.status = filters.status;
      if (filters.from) params.from = filters.from;
      if (filters.to) params.to = filters.to;
      const data = await getAnprEvents(params);
      setLoading(false);
      if (data) {
        setEvents(data.events || []);
        setPagination({ pageNo: data.pageNo, totalPages: data.totalPages, totalRecords: data.totalRecords });
      }
    },
    [filters]
  );

  useEffect(() => {
    loadEvents(1);
  }, [loadEvents]);

  const setFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));

  const openDetail = async (event) => {
    setDetail({ open: true, event, snapshotUrl: null, loadingImg: true });
    const url = await fetchAnprSnapshotObjectUrl(event.id);
    setDetail((prev) => (prev.event?.id === event.id ? { ...prev, snapshotUrl: url, loadingImg: false } : prev));
  };

  const closeDetail = () => {
    setDetail((prev) => {
      if (prev.snapshotUrl) URL.revokeObjectURL(prev.snapshotUrl);
      return { open: false, event: null, snapshotUrl: null, loadingImg: false };
    });
  };

  const laneLabel = (event) => {
    const cp = event.CapturePoint;
    if (!cp) return "—";
    const lane = cp.Lane?.name ? `${cp.Lane.name} · ` : "";
    return `${lane}${cp.type}`;
  };

  return (
    <Fragment>
      <Breadcrumb title="ANPR Events" parent="Drive-Thru" />
      <Container fluid>
        <style>{reportingGlobalCSS}</style>
        <div className="rpt-wrap">
          {/* Filters */}
          <RCard>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <select style={{ ...filterInputStyle, minWidth: 180, cursor: "pointer" }} value={filters.branchId} onChange={(e) => setFilter("branchId", e.target.value)}>
                <option value="">All branches</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
              <input style={{ ...filterInputStyle, width: 150 }} placeholder="Plate" value={filters.plate} onChange={(e) => setFilter("plate", e.target.value)} />
              <select style={{ ...filterInputStyle, minWidth: 150, cursor: "pointer" }} value={filters.status} onChange={(e) => setFilter("status", e.target.value)}>
                <option value="">All statuses</option>
                {statuses.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              <input type="date" style={filterInputStyle} value={filters.from} onChange={(e) => setFilter("from", e.target.value)} />
              <input type="date" style={filterInputStyle} value={filters.to} onChange={(e) => setFilter("to", e.target.value)} />
              <button style={coralBtn} onClick={() => loadEvents(1)}>
                Filter
              </button>
              <span style={{ marginLeft: "auto", fontSize: 12, color: C.textLight, background: C.bg, padding: "4px 12px", borderRadius: 20 }}>
                {pagination.totalRecords} events
              </span>
            </div>
          </RCard>

          {/* Table */}
          <RCard>
            {loading ? (
              <RLoader />
            ) : events.length === 0 ? (
              <div style={{ textAlign: "center", color: C.textLight, padding: 40 }}>No events match these filters.</div>
            ) : (
              <>
                <div style={{ overflowX: "auto", borderRadius: 10, border: `1px solid ${C.border}` }}>
                  <table style={tbl.table}>
                    <thead>
                      <tr>
                        <th style={tbl.th}>Time</th>
                        <th style={tbl.th}>Capture Point</th>
                        <th style={tbl.th}>Plate</th>
                        <th style={tbl.th}>Confidence</th>
                        <th style={tbl.th}>Status</th>
                        <th style={tbl.th}>Mode</th>
                      </tr>
                    </thead>
                    <tbody>
                      {events.map((event) => (
                        <tr key={event.id} className="rpt-row" style={{ cursor: "pointer" }} onClick={() => openDetail(event)}>
                          <td style={{ ...tbl.td, color: C.textMed, whiteSpace: "nowrap" }}>{new Date(event.createdAt).toLocaleString()}</td>
                          <td style={tbl.td}>{laneLabel(event)}</td>
                          <td style={{ ...tbl.td, fontWeight: 700, color: C.text }}>{event.plateNormalized || "—"}</td>
                          <td style={tbl.td}>{event.confidence != null ? `${Math.round(event.confidence * 100)}%` : "—"}</td>
                          <td style={tbl.td}>
                            <StatusChip status={event.status} />
                          </td>
                          <td style={tbl.td}>
                            {event.testMode ? <Badge color={C.blue} bg={C.blueLight}>Test</Badge> : <span style={{ color: C.textLight }}>Live</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 16 }}>
                  <span style={{ fontSize: 12, color: C.textLight }}>
                    Page {pagination.pageNo} / {pagination.totalPages || 1}
                  </span>
                  <div style={{ flex: 1 }} />
                  <button style={pageBtn(pagination.pageNo <= 1)} disabled={pagination.pageNo <= 1} onClick={() => loadEvents(pagination.pageNo - 1)}>
                    Prev
                  </button>
                  <button style={pageBtn(pagination.pageNo >= pagination.totalPages)} disabled={pagination.pageNo >= pagination.totalPages} onClick={() => loadEvents(pagination.pageNo + 1)}>
                    Next
                  </button>
                </div>
              </>
            )}
          </RCard>
        </div>
      </Container>

      <Modal isOpen={detail.open} toggle={closeDetail} size="lg" centered>
        <ModalHeader toggle={closeDetail} style={{ borderBottom: `2px solid ${C.borderLight}` }}>
          <span style={{ fontWeight: 700, color: C.text }}>ANPR Event #{detail.event?.id}</span>
        </ModalHeader>
        <ModalBody style={{ padding: 24, fontFamily: "'Inter', sans-serif" }}>
          {detail.event && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
              <div style={{ background: C.bg, borderRadius: 12, minHeight: 220, display: "flex", alignItems: "center", justifyContent: "center", border: `1px solid ${C.border}` }}>
                {detail.loadingImg ? (
                  <RLoader />
                ) : detail.snapshotUrl ? (
                  <img src={detail.snapshotUrl} alt="snapshot" style={{ maxWidth: "100%", borderRadius: 12 }} />
                ) : (
                  <span style={{ color: C.textLight }}>No snapshot available</span>
                )}
              </div>
              <div>
                {[
                  ["Plate", detail.event.plateNormalized || "—"],
                  ["Raw", detail.event.plateRaw || "—"],
                  ["Confidence", detail.event.confidence != null ? `${Math.round(detail.event.confidence * 100)}%` : "—"],
                  ["Capture Point", laneLabel(detail.event)],
                  ["Provider", detail.event.provider || "—"],
                  ["Duration", detail.event.durationMs != null ? `${detail.event.durationMs} ms` : "—"],
                  ["Time", new Date(detail.event.createdAt).toLocaleString()],
                ].map(([label, value]) => (
                  <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "9px 0", borderBottom: `1px solid ${C.borderLight}` }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: C.textLight, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{value}</span>
                  </div>
                ))}
                <div style={{ display: "flex", justifyContent: "space-between", padding: "9px 0", alignItems: "center" }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: C.textLight, textTransform: "uppercase", letterSpacing: 0.5 }}>Status</span>
                  <StatusChip status={detail.event.status} />
                </div>
              </div>
            </div>
          )}
        </ModalBody>
      </Modal>
    </Fragment>
  );
};

export default AnprEvents;
